/**
 * Внешний (системный) провайдер.
 * Каждое значение в `EditModal` соответствует отдельному набору полей конфигурации
 * (по аналогии с `PaymentSystem.PaymentProvider`).
 */
export enum ReceiptSystemProviderEnum {
	FISCAL_PARTNER = "fiscal_partner",
	MONOBANK = "monobank",
	/** Checkbox (Украина, ПРРО): пин-код кассира и ключ лицензии кассы. */
	CHECKBOX = "checkbox",
}

/** Вход кассира Checkbox: пин-код + ключ лицензии кассы. */
export enum CheckboxAuthMethod {
	PIN = "pin",
}

/**
 * Куда привязан провайдер: к водителю или к транспорту.
 * Радио-выбор в EditModal — нельзя выбрать оба сразу.
 */
export enum ReceiptBindingTarget {
	EXECUTOR = "EXECUTOR",
	CAR = "CAR",
}

/**
 * Источник заказа, для которого включается отправка чека.
 * Маппинг из источника заказа на бэке: клиентское приложение → MOBILE_APP,
 * заказ создан исполнителем → EXECUTOR, остальное → DISPATCHER.
 */
export enum ReceiptOrderSource {
	DISPATCHER = "DISPATCHER",
	MOBILE_APP = "MOBILE_APP",
	EXECUTOR = "EXECUTOR",
}

/**
 * Статус транзакции отправки чека (журнал в History tab).
 */
export enum ReceiptStatus {
	PENDING = "PENDING",
	SUCCESS = "SUCCESS",
	FAILED = "FAILED",
	SKIPPED = "SKIPPED",
}

export enum ReceiptSkipReason {
	NO_PROVIDER = "NO_PROVIDER",
	DISABLED_ON_EXECUTOR = "DISABLED_ON_EXECUTOR",
	DISABLED_ON_CAR = "DISABLED_ON_CAR",
	UNSUPPORTED_PAYMENT_TYPE = "UNSUPPORTED_PAYMENT_TYPE",
	UNSUPPORTED_ORDER_SOURCE = "UNSUPPORTED_ORDER_SOURCE",
	PROVIDER_INACTIVE = "PROVIDER_INACTIVE",
	MISSING_DRIVER_TAX_ID = "MISSING_DRIVER_TAX_ID",
}

export interface ReceiptSummary {
	status: ReceiptStatus;
	skipReason?: ReceiptSkipReason | null;
	transactionId?: number | null;
	externalId?: string | null;
}

/**
 * Способ оплаты для матчинга провайдера чеков (= `order.paymentType`).
 * Бонусные/агентские скидки в чек попадают через `cost.discount`, не через этот enum.
 */
export enum ReceiptOrderPaymentType {
	CARD = "card",
	CASH = "cash",
	COMPANY_BILL = "company_bill",
	INVOICE = "invoice",
	/** @deprecated Legacy DB value; stripped on read/write */
	BONUSES = "bonuses",
	/** @deprecated Legacy DB value; stripped on read/write */
	AGENTS = "agents",
}

/**
 * Перша стаття синтетичного чека власного замовлення.
 * Сума береться з тарифу класу авто, не з модалки.
 */
export enum OwnOrderReceiptFirstItem {
	BOARDING = "boarding",
	MIN_ORDER_COST = "minOrderCost",
}

/**
 * Куди йде залишок після посадки/мінімалки та фіксованих км.
 */
export enum OwnOrderReceiptRemainderTarget {
	SERVICE = "service",
	TRAVEL_TIME = "travelTime",
	MARKUP = "markup",
}

export interface OwnOrderReceiptSettings {
	firstItem: OwnOrderReceiptFirstItem;
	fixedKm: number;
	remainderTarget: OwnOrderReceiptRemainderTarget;
	serviceNames: Record<string, string>;
}

export const DEFAULT_OWN_ORDER_RECEIPT: OwnOrderReceiptSettings = {
	firstItem: OwnOrderReceiptFirstItem.BOARDING,
	fixedKm: 1,
	remainderTarget: OwnOrderReceiptRemainderTarget.TRAVEL_TIME,
	serviceNames: {},
};

/** Провайдеры с синтетическим чеком собственного заказа: ECC и Checkbox. */
export function supportsOwnOrderReceipt(
	provider: string | null | undefined,
): boolean {
	return (
		provider === ReceiptSystemProviderEnum.FISCAL_PARTNER ||
		provider === ReceiptSystemProviderEnum.CHECKBOX
	);
}

export function normalizeOwnOrderReceipt(
	value: Partial<OwnOrderReceiptSettings> | null | undefined,
): OwnOrderReceiptSettings {
	const remainderTarget =
		value?.remainderTarget === OwnOrderReceiptRemainderTarget.SERVICE ||
		value?.remainderTarget === OwnOrderReceiptRemainderTarget.TRAVEL_TIME ||
		value?.remainderTarget === OwnOrderReceiptRemainderTarget.MARKUP
			? value.remainderTarget
			: DEFAULT_OWN_ORDER_RECEIPT.remainderTarget;

	return {
		firstItem:
			value?.firstItem === OwnOrderReceiptFirstItem.MIN_ORDER_COST
				? OwnOrderReceiptFirstItem.MIN_ORDER_COST
				: OwnOrderReceiptFirstItem.BOARDING,
		fixedKm:
			typeof value?.fixedKm === "number" &&
			Number.isFinite(value.fixedKm) &&
			value.fixedKm > 0
				? value.fixedKm
				: DEFAULT_OWN_ORDER_RECEIPT.fixedKm,
		remainderTarget,
		serviceNames:
			value?.serviceNames && typeof value.serviceNames === "object"
				? { ...value.serviceNames }
				: {},
	};
}

export const RECEIPT_CORE_ORDER_PAYMENT_TYPES = [
	ReceiptOrderPaymentType.CASH,
	ReceiptOrderPaymentType.CARD,
	ReceiptOrderPaymentType.COMPANY_BILL,
] as const;

const RECEIPT_CORE_ORDER_PAYMENT_TYPE_SET = new Set<string>(
	RECEIPT_CORE_ORDER_PAYMENT_TYPES,
);

export function normalizeReceiptOrderPaymentTypes(
	types: ReceiptOrderPaymentType[] | null | undefined,
): ReceiptOrderPaymentType[] {
	if (!Array.isArray(types)) return [];
	return types.filter((type) =>
		RECEIPT_CORE_ORDER_PAYMENT_TYPE_SET.has(type),
	);
}

/**
 * Касса Checkbox (режим нескольких касс, `configuration.multipleRegisters`).
 * Водитель/машина привязан ровно к одной кассе; не назначенные работают
 * через первую кассу провайдера.
 */
export interface ReceiptCashRegister {
	id?: number;
	name: string;
	licenseKey: string;
	pinCode: string;
	active: boolean;
	/** Водители кассы (`bindingTarget = EXECUTOR`). */
	executorIds: number[];
	/** Машины кассы (`bindingTarget = CAR`). */
	carIds: number[];
}
